import React, { useRef, useState } from "react"
import { Table, Progress } from "reactstrap"
import Button from "components/shared/Button/Button"
import api from "../../services/api"
import Swal from "sweetalert2"
import {
  parseSpreadsheet,
  getStoredImovelMatchKeys,
  isRowAlreadyRegistered,
} from "../../utils/condominioImporter/parseHidrometroSpreadsheet"
import { downloadHidrometroTemplate } from "../../utils/condominioImporter/buildHidrometroTemplate"

const ImovelImportadorXlsx = ({ condoId }) => {
  const fileInputRef = useRef(null)
  const [rows, setRows] = useState([])
  const [parseErrors, setParseErrors] = useState([])
  const [pulsoPadrao, setPulsoPadrao] = useState("1")
  const [importing, setImporting] = useState(false)
  const [progress, setProgress] = useState(0)
  const [fileName, setFileName] = useState("")
  const [skippedMacro, setSkippedMacro] = useState(0)
  const [alreadyRegisteredCount, setAlreadyRegisteredCount] = useState(0)

  const fetchExistingImoveis = async () => {
    const byOid = new Map()

    const mergeList = list => {
      const items = Array.isArray(list) ? list : []
      items.forEach(elem => {
        if (elem?.OID_IMOVEL) {
          byOid.set(elem.OID_IMOVEL, elem)
        }
      })
    }

    const [allRes, vagosRes] = await Promise.allSettled([
      api.get(`/imovel/${condoId}`),
      api.get(`/imovel/vagos/${condoId}`),
    ])

    if (allRes.status === "fulfilled") mergeList(allRes.value.data)
    if (vagosRes.status === "fulfilled") mergeList(vagosRes.value.data)

    if (allRes.status === "rejected" && vagosRes.status === "rejected") {
      throw new Error(
        "Falha ao consultar os imóveis já cadastrados neste condomínio.",
      )
    }

    const keys = new Set()
    byOid.forEach(elem => {
      getStoredImovelMatchKeys(elem).forEach(key => keys.add(key))
    })
    return keys
  }

  const annotateExistingRows = (parsedRows, existingKeys) => {
    let registered = 0
    const annotated = parsedRows.map(row => {
      const alreadyExists = isRowAlreadyRegistered(row, existingKeys)
      if (alreadyExists) registered++
      return { ...row, alreadyExists }
    })
    setAlreadyRegisteredCount(registered)
    return annotated
  }

  const refreshRowsAgainstDatabase = async parsedRows => {
    const existingKeys = await fetchExistingImoveis()
    return annotateExistingRows(parsedRows, existingKeys)
  }

  const handleDownloadTemplate = async event => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    try {
      await downloadHidrometroTemplate()
    } catch (err) {
      console.error(err)
      Swal.fire({
        icon: "error",
        text: "Não foi possível gerar a planilha modelo. Tente novamente ou recarregue a página.",
      })
    }
  }

  const handleFileChange = async event => {
    const file = event.target.files?.[0]
    if (!file) return

    setFileName(file.name)
    setRows([])
    setParseErrors([])
    setProgress(0)
    setSkippedMacro(0)
    setAlreadyRegisteredCount(0)

    try {
      const buffer = await file.arrayBuffer()
      const {
        rows: parsedRows,
        errors,
        skippedMacro: macroCount = 0,
      } = await parseSpreadsheet(buffer)
      setSkippedMacro(macroCount)

      if (!condoId) {
        setRows(parsedRows)
        setParseErrors(errors)
        return
      }

      try {
        const existingKeys = await fetchExistingImoveis()
        const annotatedRows = annotateExistingRows(parsedRows, existingKeys)
        setRows(annotatedRows)
      } catch (fetchErr) {
        console.error(fetchErr)
        setRows(parsedRows)
        setParseErrors([
          ...errors,
          "Não foi possível consultar os imóveis já cadastrados neste condomínio. A planilha foi lida, mas o status 'já cadastrado' pode estar incompleto.",
        ])
        return
      }

      setParseErrors(errors)
    } catch (err) {
      console.error(err)
      Swal.fire({
        icon: "error",
        text: "Não foi possível ler o arquivo. Envie um .xlsx com as colunas COMP e Nº HD na primeira aba.",
      })
    } finally {
      event.target.value = ""
    }
  }

  const validRows = rows.filter(r => r.valid)
  const importableRows = validRows.filter(r => !r.alreadyExists)
  const invalidRowsCount = rows.length - validRows.length

  const getRowClassName = row => {
    if (!row.valid) return "table-danger"
    if (row.alreadyExists) return "table-warning"
    return ""
  }

  const getRowStatus = row => {
    if (!row.valid) return row.error || "Linha inválida"
    if (row.alreadyExists) {
      return "Já cadastrado neste condomínio (AP ou código de imóvel)"
    }
    return "Novo"
  }

  const importRow = async row => {
    const imovelBody = {
      OID_CONDOMINIOATUAL: Number(condoId),
      IM_COMPLEMENTO: row.IM_COMPLEMENTO,
      IM_BLOCO: row.IM_BLOCO,
      CD_IMOVEL: row.cdImovel || "",
    }

    const imovelRes = await api.post("/imovel/imovel", imovelBody)

    const hidroBody = {
      OID_IMOVEL: imovelRes.data.OID_IMOVEL,
      NR_MEDIDOR: row.nrMedidor,
      NR_CONSUMO_LITROS: pulsoPadrao,
      TP_ATIVO: "S",
      DS_DESCRICAO_MEDIDOR: "",
      DS_MODELO_MEDIDOR: "Novo Modelo",
    }

    await api.post("/hydrometers", hidroBody)
  }

  const getRowErrorMessage = (error, row) => {
    const status = error.response?.status
    const apiError = error.response?.data?.error
    const apiMessage = error.response?.data?.message
    const compLabel = row.compOriginal || row.IM_COMPLEMENTO || "sem COMP"
    const hdLabel = row.nrMedidor || "sem Nº HD"

    if (status === 409) {
      if (
        typeof apiMessage === "string" &&
        apiMessage.toLowerCase().includes("código")
      ) {
        return `Código de imóvel já cadastrado (${row.cdImovel || "vazio"}). AP ${compLabel}.`
      }
      return (
        apiMessage ||
        `Já existe um AP cadastrado neste condomínio com COMP ${compLabel}.`
      )
    }

    if (apiError === "NR_MEDIDOR already exists") {
      return `O hidrômetro ${hdLabel} já existe no sistema. Não é possível cadastrar o mesmo Nº HD duas vezes.`
    }

    if (status === 400 || status === 422) {
      return `Não foi possível cadastrar o AP ${compLabel} (HD ${hdLabel}). O imóvel já possui medidor ou os dados foram recusados.`
    }

    if (!error.response) {
      return `Falha de conexão ao gravar o AP ${compLabel} (HD ${hdLabel}). Verifique a rede e tente novamente.`
    }

    return `Erro ao gravar o AP ${compLabel} (HD ${hdLabel}).`
  }

  const handleImport = async event => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (importing) return

    if (!condoId) {
      Swal.fire({
        icon: "warning",
        title: "Condomínio não identificado",
        text: "Abra Cadastrar IMÓVEL a partir da lista do condomínio para importar os hidrômetros nesse local.",
      })
      return
    }

    if (!pulsoPadrao || pulsoPadrao.trim() === "" || pulsoPadrao.trim() === "0") {
      Swal.fire({
        icon: "warning",
        title: "Pulso/Litro obrigatório",
        text: "Informe um Pulso/Litro padrão maior que zero. Esse valor será aplicado a todos os hidrômetros importados.",
      })
      return
    }

    if (importableRows.length === 0) {
      let emptyReason =
        "Nenhuma linha válida para importar. Confira COMP (AP201 ou AP101B1) e Nº HD na planilha."
      if (validRows.length > 0) {
        emptyReason =
          "Todos os APs válidos da planilha já estão cadastrados neste condomínio. Nada novo para importar."
      } else if (invalidRowsCount > 0) {
        emptyReason =
          "Há linhas na planilha, mas nenhuma é válida. Veja as linhas em vermelho: COMP deve ser AP201 ou AP101B1, e o Nº HD não pode estar vazio."
      }
      Swal.fire({
        icon: "warning",
        title: "Nada para importar",
        text: emptyReason,
      })
      return
    }

    let existingKeys
    try {
      existingKeys = await fetchExistingImoveis()
    } catch (fetchErr) {
      console.error(fetchErr)
      Swal.fire({
        icon: "error",
        title: "Falha ao consultar o condomínio",
        text: "Não foi possível buscar os imóveis já cadastrados. Tente novamente em instantes.",
      })
      return
    }

    const rowsToImport = importableRows.filter(
      row => !isRowAlreadyRegistered(row, existingKeys)
    )

    if (rowsToImport.length === 0) {
      Swal.fire({
        icon: "info",
        title: "Nada novo para importar",
        text: "Todos os APs válidos da planilha já foram cadastrados neste condomínio desde a pré-visualização.",
      })
      return
    }

    const skippedRegistered = validRows.length - rowsToImport.length

    const confirm = await Swal.fire({
      icon: "question",
      title: "Confirmar importação",
      text: `Importar ${rowsToImport.length} imóvel(is) com medidor(es)?${
        skippedRegistered > 0
          ? ` (${skippedRegistered} já cadastrado(s) será(ão) ignorado(s))`
          : ""
      }`,
      showCancelButton: true,
      confirmButtonText: "Importar",
      cancelButtonText: "Cancelar",
    })

    if (!confirm.isConfirmed) return

    setImporting(true)
    setProgress(0)

    const results = { success: 0, failed: [], skipped: skippedRegistered }
    const total = rowsToImport.length

    for (let i = 0; i < rowsToImport.length; i++) {
      const row = rowsToImport[i]
      try {
        await importRow(row)
        results.success++
      } catch (error) {
        results.failed.push({
          ordem: i + 1,
          comp: row.IM_COMPLEMENTO,
          medidor: row.nrMedidor,
          message: getRowErrorMessage(error, row),
        })
      }
      setProgress(Math.round(((i + 1) / total) * 100))
    }

    setImporting(false)

    try {
      const refreshedRows = await refreshRowsAgainstDatabase(rows)
      setRows(refreshedRows)
    } catch (refreshErr) {
      console.error(refreshErr)
    }

    const failedSummary =
      results.failed.length > 0
        ? `<br/><br/><b>Falhas (${results.failed.length}):</b><br/>${results.failed
            .slice(0, 10)
            .map(f => `#${f.ordem} (${f.comp}): ${f.message}`)
            .join("<br/>")}${results.failed.length > 10 ? "<br/>..." : ""}`
        : ""

    const skippedSummary =
      results.skipped > 0
        ? `<br/>Ignorados (já cadastrados): <b>${results.skipped}</b>.`
        : ""

    Swal.fire({
      icon: results.failed.length === 0 ? "success" : "warning",
      title: "Importação concluída",
      html: `Importados com sucesso: <b>${results.success}</b> de <b>${total}</b>.${skippedSummary}${failedSummary}`,
    })
  }

  return (
    <div className="mt-4 pt-4" style={{ borderTop: "2px solid #e9ecef" }}>
      <h5 className="mb-3">Importar via XLSX</h5>
      <p className="text-muted mb-3">
        Baixe a planilha modelo, preencha COMP (AP101B1 → APT1 101 / BLOCO 1,
        ou AP201 → APTO 201), Nº HD e Código de Imóvel, e importe o arquivo.
      </p>

      <div className="row mb-3 align-items-end">
        <div className="col-md-5">
          <label className="form-label">Arquivo XLSX</label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="form-control"
            onChange={handleFileChange}
            disabled={importing}
          />
          {fileName && (
            <small className="text-muted d-block mt-1">{fileName}</small>
          )}
        </div>
        <div className="col-md-4 mb-1">
          <Button
            icon="ti-download"
            text="Baixar planilha modelo"
            action={handleDownloadTemplate}
            buttonType="button"
            primaryColor="#5d4ec0"
            hoverColor="#4eadc0"
            styles={{ width: "auto", minWidth: "12rem", padding: "0 1rem" }}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">Pulso/Litro padrão *</label>
          <input
            type="text"
            className="form-control"
            value={pulsoPadrao}
            onChange={e => setPulsoPadrao(e.target.value)}
            disabled={importing}
          />
        </div>
      </div>

      {parseErrors.length > 0 && (
        <div className="alert alert-warning py-2">
          {parseErrors.map((err, i) => (
            <div key={i}>{err}</div>
          ))}
        </div>
      )}

      {(rows.length > 0 || skippedMacro > 0) && (
        <>
          <div
            className="mb-3 p-3 rounded"
            style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}
          >
            <div>
              <strong>Total: {importableRows.length}</strong> registro(s) para
              importação
            </div>
            {(skippedMacro > 0 ||
              alreadyRegisteredCount > 0 ||
              invalidRowsCount > 0) && (
              <div className="text-muted mt-1" style={{ fontSize: "0.9rem" }}>
                {skippedMacro > 0 && (
                  <span>
                    {skippedMacro} MACRO ignorado(s) (medidor geral, não é AP).{" "}
                  </span>
                )}
                {alreadyRegisteredCount > 0 && (
                  <span>
                    {alreadyRegisteredCount} já cadastrado(s) neste
                    condomínio (mesmo AP ou código de imóvel).{" "}
                  </span>
                )}
                {invalidRowsCount > 0 && (
                  <span>
                    {invalidRowsCount} inválido(s): veja o status em vermelho
                    (COMP, Nº HD ou duplicado na planilha).{" "}
                  </span>
                )}
              </div>
            )}
            {rows.length > 0 && (
              <div className="mt-2" style={{ fontSize: "0.85rem" }}>
                <span
                  className="d-inline-block me-3 px-2 py-1 rounded"
                  style={{ background: "#fff3cd" }}
                >
                  Amarelo: já cadastrado
                </span>
                <span
                  className="d-inline-block px-2 py-1 rounded"
                  style={{ background: "#f8d7da" }}
                >
                  Vermelho: inválido
                </span>
              </div>
            )}
          </div>
          {rows.length > 0 && (
            <div style={{ maxHeight: "320px", overflowY: "auto" }}>
              <Table bordered size="sm" responsive className="mb-3">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Nº AP</th>
                    <th>Bloco</th>
                    <th>Código de Imóvel</th>
                    <th>Pulso</th>
                    <th>Hidrômetro</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr
                      key={`${row.rowNum}-${index}`}
                      className={getRowClassName(row)}
                    >
                      <td>{index + 1}</td>
                      <td>
                        {row.valid
                          ? row.IM_COMPLEMENTO
                          : row.compOriginal || "-"}
                      </td>
                      <td>
                        {row.valid && row.IM_BLOCO
                          ? `BLOCO ${row.IM_BLOCO}`
                          : "-"}
                      </td>
                      <td>{row.cdImovel || "-"}</td>
                      <td>{row.valid ? pulsoPadrao : "-"}</td>
                      <td>{row.nrMedidor || "-"}</td>
                      <td>{getRowStatus(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </>
      )}

      {importing && (
        <Progress value={progress} className="mb-3">
          {progress}%
        </Progress>
      )}

      <div style={{ textAlign: "center" }}>
        <Button
          icon="ti-upload"
          text={importing ? "Importando..." : "Importar"}
          action={handleImport}
          buttonType="button"
          primaryColor="#5d4ec0"
          hoverColor="#4eadc0"
          styles={{
            opacity: importing || importableRows.length === 0 ? 0.6 : 1,
            pointerEvents:
              importing || importableRows.length === 0 ? "none" : "auto",
          }}
        />
      </div>
    </div>
  )
}

export default ImovelImportadorXlsx
